import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import '@/components/mapbox-overrides.css';
import { Loader2, MapPin, AlertCircle, RotateCcw, Layers, Pencil, Check, Undo2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
mapboxgl.accessToken = MAPBOX_TOKEN;

// Geocode a free-form address using Mapbox Geocoding API v5 (forward).
// Returns { center, place_name, bbox, accuracy, relevance, place_type } — useful for
// surfacing a confidence chip to the user before they run AI estimate.
export async function geocodeAddress(address) {
    if (!MAPBOX_TOKEN) throw new Error('REACT_APP_MAPBOX_TOKEN not configured');
    if (!address) return null;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&limit=1&country=US&types=address,poi`;
    const r = await fetch(url);
    if (!r.ok) throw new Error(`Geocoding failed: ${r.status}`);
    const data = await r.json();
    const feat = (data.features || [])[0];
    if (!feat) return null;
    return {
        center: feat.center,
        place_name: feat.place_name,
        bbox: feat.bbox,
        relevance: feat.relevance,                           // 0..1, Mapbox match confidence
        accuracy: feat.properties?.accuracy,                  // 'rooftop' | 'parcel' | 'point' | 'interpolated' | 'street' | etc
        place_type: feat.place_type?.[0],
    };
}

// Map raw geocoder accuracy → human-friendly label + colour
export function describeGeocodeAccuracy(geo) {
    if (!geo) return { label: 'Unknown', tone: 'low', detail: 'Could not geocode this address.' };
    const a = geo.accuracy || '';
    const rel = geo.relevance ?? 0;
    if (a === 'rooftop') return { label: 'Rooftop-accurate', tone: 'high', detail: 'Geocoder pinpointed the exact rooftop.' };
    if (a === 'parcel') return { label: 'Parcel-accurate', tone: 'high', detail: 'Geocoder placed the pin on the parcel.' };
    if (a === 'point') return { label: 'Point-accurate', tone: 'medium', detail: 'Geocoder snapped to the address point. Verify the pin is on the right roof.' };
    if (a === 'interpolated') return { label: 'Interpolated', tone: 'low', detail: 'Address was interpolated along the street — pin may not be on the actual house. Drag the pin onto the rooftop.' };
    if (a === 'street') return { label: 'Street-level', tone: 'low', detail: 'Only the street was matched. Manually center on the home before running AI.' };
    if (rel >= 0.9) return { label: 'High match', tone: 'high', detail: 'Strong address match.' };
    if (rel >= 0.7) return { label: 'Medium match', tone: 'medium', detail: 'Decent address match. Verify the pin.' };
    return { label: 'Weak match', tone: 'low', detail: 'Address is fuzzy — verify the rooftop manually.' };
}

/**
 * Compute area of a polygon in square meters using the shoelace formula on
 * the equirectangular projection — accurate enough for residential parcels.
 *
 * coords: array of [lng, lat] pairs (NOT closed; first ≠ last)
 */
function polygonAreaSqMeters(coords) {
    if (!coords || coords.length < 3) return 0;
    const R = 6378137; // earth radius in meters
    const toRad = (d) => (d * Math.PI) / 180;
    let area = 0;
    for (let i = 0, n = coords.length; i < n; i++) {
        const [lng1, lat1] = coords[i];
        const [lng2, lat2] = coords[(i + 1) % n];
        area += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
    }
    area = (area * R * R) / 2.0;
    return Math.abs(area);
}

const SQM_TO_SQFT = 10.7639;
// Pitch multiplier — how much actual roof surface area exceeds the 2D footprint
const PITCH_MULTIPLIER = {
    low: 1.06,    // ≈ 4/12 slope
    medium: 1.12, // ≈ 6/12
    steep: 1.25,  // ≈ 9/12
};

function computeMeasurements(features, defaultPitch = 'medium') {
    const polys = (features || []).filter((f) => f.geometry?.type === 'Polygon');
    let footprintSqft = 0;
    for (const f of polys) {
        // Polygon coordinates: [ [outer-ring], [hole], ...]
        const outer = f.geometry.coordinates[0] || [];
        // The Mapbox-draw polygon ring is closed (first==last); drop trailing duplicate
        const ring = outer.length > 1 && outer[0][0] === outer[outer.length - 1][0] && outer[0][1] === outer[outer.length - 1][1]
            ? outer.slice(0, -1)
            : outer;
        footprintSqft += polygonAreaSqMeters(ring) * SQM_TO_SQFT;
    }
    const pitch = defaultPitch in PITCH_MULTIPLIER ? defaultPitch : 'medium';
    const roofSqft = footprintSqft * PITCH_MULTIPLIER[pitch];
    return {
        footprint_sqft: Math.round(footprintSqft),
        roof_sqft: Math.round(roofSqft),
        facets_count: polys.length,
        pitch,
    };
}

// Haversine distance between two [lng, lat] points, in meters.
// Used to warn the contractor if they've dragged the map pin far from the
// geocoded parcel centroid — likely indicating they're on the wrong rooftop.
function haversineMeters(a, b) {
    if (!a || !b) return 0;
    const R = 6378137; // earth radius in meters
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b[1] - a[1]);
    const dLng = toRad(b[0] - a[0]);
    const lat1 = toRad(a[1]);
    const lat2 = toRad(b[1]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * RoofMapView — satellite Mapbox canvas with polygon draw tools.
 *
 * Props:
 *  - address: string                            (used to geocode + center the map)
 *  - initial: { center, zoom, polygons }        (existing saved geometry)
 *  - readOnly: bool                             (LeadDetail uses true; QuoteBuilder false)
 *  - onChange: (geometry) => void               (debounced when drawing changes)
 *  - height: string (default 420px)
 */
export const RoofMapView = forwardRef(function RoofMapView({ address, initial, readOnly = false, onChange, height = '420px' }, ref) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const drawRef = useRef(null);
    const pinMarkerRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [style, setStyle] = useState('satellite-streets');
    const [measurements, setMeasurements] = useState(null);
    const [defaultPitch, setDefaultPitch] = useState((initial?.measurements?.pitch) || 'medium');
    const [geocodeInfo, setGeocodeInfo] = useState(null);   // result of geocodeAddress
    const [pinLngLat, setPinLngLat] = useState(initial?.center || null);  // current draggable pin position
    const originalCenterRef = useRef(null);  // first-known parcel center (geocoded or RentCast); used for drift warning
    const [pinDriftM, setPinDriftM] = useState(0);          // meters between current pin and original parcel center
    // Track MapboxDraw mode for UX hints. The stock MapboxDraw plugin has zero
    // affordance for "I'm done drawing" — the user has to click the first
    // point or double-click to close a polygon, which roofers don't know.
    // We expose a friendlier "Finish" / "Cancel" / "Undo last point" toolbar
    // and a clear inline hint while drawing.
    const [drawMode, setDrawMode] = useState('simple_select');  // simple_select | draw_polygon

    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    const STYLE_URLS = {
        'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
        'satellite': 'mapbox://styles/mapbox/satellite-v9',
        'standard': 'mapbox://styles/mapbox/standard',
    };

    // Convert saved polygons → GeoJSON FeatureCollection
    const initialFeatures = useCallback(() => {
        const out = [];
        for (const p of (initial?.polygons || [])) {
            if (!p?.coords || p.coords.length < 3) continue;
            // Close ring
            const ring = p.coords[0][0] === p.coords[p.coords.length - 1][0] && p.coords[0][1] === p.coords[p.coords.length - 1][1]
                ? p.coords
                : [...p.coords, p.coords[0]];
            out.push({
                type: 'Feature',
                properties: { label: p.label, pitch: p.pitch },
                geometry: { type: 'Polygon', coordinates: [ring] },
            });
        }
        return out;
    }, [initial]);

    const emitChange = useCallback(() => {
        if (!drawRef.current || !mapRef.current) return;
        const data = drawRef.current.getAll();
        const features = data.features || [];
        const ms = computeMeasurements(features, defaultPitch);
        setMeasurements(ms);
        const center = mapRef.current.getCenter();
        const zoom = mapRef.current.getZoom();
        const polygons = features
            .filter((f) => f.geometry?.type === 'Polygon')
            .map((f) => {
                const outer = f.geometry.coordinates[0] || [];
                const ring = outer.length > 1 && outer[0][0] === outer[outer.length - 1][0] && outer[0][1] === outer[outer.length - 1][1]
                    ? outer.slice(0, -1)
                    : outer;
                return { coords: ring, label: f.properties?.label || null, pitch: f.properties?.pitch || defaultPitch };
            });
        if (onChangeRef.current) {
            onChangeRef.current({
                center: [center.lng, center.lat],
                zoom,
                polygons,
                measurements: ms,
                source: polygons.length === 0 ? 'manual' : (initial?.source === 'ai_vision' ? 'edited' : 'manual'),
            });
        }
    }, [defaultPitch, initial]);

    // Initialize map + draw control
    useEffect(() => {
        if (!MAPBOX_TOKEN) {
            setError('Mapbox token not configured. Set REACT_APP_MAPBOX_TOKEN in frontend/.env');
            setLoading(false);
            return;
        }
        let cancelled = false;
        let map;

        (async () => {
            try {
                let center = initial?.center;
                let zoom = initial?.zoom || 19;
                let geoResult = null;
                if (!center && address) {
                    geoResult = await geocodeAddress(address);
                    if (geoResult?.center) {
                        center = geoResult.center;
                        setGeocodeInfo(geoResult);
                    }
                }
                if (!center) center = [-95.7129, 37.0902]; // Continental US fallback
                if (cancelled) return;
                setPinLngLat(center);
                // Remember the *original* parcel/geocoded center so we can warn if
                // the contractor drags the pin onto a neighbouring building.
                originalCenterRef.current = center;

                map = new mapboxgl.Map({
                    container: containerRef.current,
                    style: STYLE_URLS[style],
                    center,
                    zoom,
                    pitchWithRotate: false,
                    attributionControl: false,
                });
                mapRef.current = map;

                map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
                map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

                // Draggable "refine center" pin — appears as an amber marker the user
                // can drag onto the actual rooftop before running AI estimate.
                // This dramatically improves accuracy when the geocoder is wrong.
                if (!readOnly) {
                    const el = document.createElement('div');
                    el.className = 'qf-roof-pin';
                    el.title = 'Drag onto the rooftop you want to analyze';
                    el.setAttribute('data-testid', 'roofmap-refine-pin');
                    const marker = new mapboxgl.Marker({ element: el, draggable: true, anchor: 'center' })
                        .setLngLat(center)
                        .addTo(map);
                    marker.on('drag', () => {
                        const ll = marker.getLngLat();
                        setPinLngLat([ll.lng, ll.lat]);
                        if (originalCenterRef.current) {
                            setPinDriftM(haversineMeters(originalCenterRef.current, [ll.lng, ll.lat]));
                        }
                    });
                    marker.on('dragend', () => {
                        const ll = marker.getLngLat();
                        // Recenter the map onto the new pin for a smooth UX
                        map.flyTo({ center: [ll.lng, ll.lat], zoom: map.getZoom(), essential: true });
                        if (originalCenterRef.current) {
                            setPinDriftM(haversineMeters(originalCenterRef.current, [ll.lng, ll.lat]));
                        }
                    });
                    pinMarkerRef.current = marker;
                }

                if (!readOnly) {
                    const draw = new MapboxDraw({
                        displayControlsDefault: false,
                        controls: { polygon: false, trash: false },  // we render our own toolbar
                        defaultMode: 'simple_select',
                        styles: drawStyles,
                    });
                    drawRef.current = draw;
                    map.addControl(draw, 'top-left');

                    map.on('draw.create', emitChange);
                    map.on('draw.update', emitChange);
                    map.on('draw.delete', emitChange);
                    map.on('draw.modechange', (e) => setDrawMode(e.mode || 'simple_select'));
                }

                map.on('load', () => {
                    // Defensive resize in case the container was 0-sized at init
                    map.resize();
                    // Seed with existing polygons
                    if (drawRef.current && initial?.polygons?.length) {
                        drawRef.current.set({ type: 'FeatureCollection', features: initialFeatures() });
                    } else if (readOnly && initial?.polygons?.length) {
                        // Render polygons as a static layer for read-only views
                        map.addSource('qf-roof-static', {
                            type: 'geojson',
                            data: { type: 'FeatureCollection', features: initialFeatures() },
                        });
                        map.addLayer({
                            id: 'qf-roof-fill',
                            type: 'fill',
                            source: 'qf-roof-static',
                            paint: { 'fill-color': '#F59E0B', 'fill-opacity': 0.25 },
                        });
                        map.addLayer({
                            id: 'qf-roof-outline',
                            type: 'line',
                            source: 'qf-roof-static',
                            paint: { 'line-color': '#F59E0B', 'line-width': 2 },
                        });
                    }
                    setMeasurements(computeMeasurements(initialFeatures(), defaultPitch));
                    setLoading(false);
                });

                // Watch container size changes (parent tab switching, responsive resize)
                const ro = new ResizeObserver(() => {
                    try { map.resize(); } catch (_e) { /* noop */ }
                });
                ro.observe(containerRef.current);
                map.__qfResizeObserver = ro;

                map.on('error', (e) => {
                    // Only show the error for fatal load failures, not minor tile issues
                    const msg = e?.error?.message || '';
                    if (msg.includes('Unauthorized') || msg.includes('Forbidden') || msg.includes('access token')) {
                        setError('Map authentication failed. Check Mapbox token.');
                    }
                });
            } catch (e) {
                setError(e.message || 'Failed to load map');
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            try {
                if (mapRef.current) {
                    try { mapRef.current.__qfResizeObserver?.disconnect(); } catch (_e) { /* noop */ }
                    mapRef.current.remove();
                    mapRef.current = null;
                    drawRef.current = null;
                }
            } catch (_e) { /* noop */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address, style]);

    // Recompute measurements when pitch changes
    useEffect(() => {
        if (!drawRef.current) return;
        emitChange();
    }, [defaultPitch, emitChange]);

    // Imperative API for parent (e.g., QuoteBuilder) to grab live map state
    // and trigger AI estimation without breaking the encapsulation of MapboxDraw.
    useImperativeHandle(ref, () => ({
        getMapState: () => {
            if (!mapRef.current) return null;
            // Prefer the user-positioned pin location (so AI runs on the rooftop
            // the contractor actually centered on, not just the map's viewport).
            const pin = pinMarkerRef.current?.getLngLat();
            const c = pin ? { lng: pin.lng, lat: pin.lat } : mapRef.current.getCenter();
            const z = mapRef.current.getZoom();
            return { center: [c.lng, c.lat], zoom: z, source: pin ? 'pin' : 'map_center' };
        },
        getCanvasImageBase64: () => {
            try {
                const canvas = containerRef.current?.querySelector('canvas.mapboxgl-canvas');
                if (!canvas) return null;
                const dataUrl = canvas.toDataURL('image/png');
                if (dataUrl && dataUrl.length > 200) return dataUrl.split(',', 2)[1];
                return null;
            } catch (_e) {
                return null;
            }
        },
        recenterOnPin: () => {
            const pin = pinMarkerRef.current?.getLngLat();
            if (pin && mapRef.current) {
                mapRef.current.flyTo({ center: [pin.lng, pin.lat], zoom: 19, essential: true });
            }
        },
        getGeocodeInfo: () => geocodeInfo,
        resize: () => { try { mapRef.current?.resize(); } catch (_e) { /* noop */ } },
    }), [geocodeInfo]);

    const resetView = () => {
        if (!mapRef.current || !address) return;
        geocodeAddress(address).then((g) => {
            if (g?.center) mapRef.current.flyTo({ center: g.center, zoom: 19, essential: true });
        }).catch(() => {});
    };

    // ── Polygon drawing UX helpers ───────────────────────────────────────────
    // Stock MapboxDraw exposes nothing user-friendly to start/finish/cancel,
    // so we wrap those operations behind clearly-labelled buttons that match
    // contractor expectations ("Draw roof", "Finish", "Undo last point", etc).
    const startDrawing = () => {
        if (!drawRef.current) return;
        drawRef.current.changeMode('draw_polygon');
        setDrawMode('draw_polygon');
    };
    const finishDrawing = () => {
        if (!drawRef.current) return;
        // changing back to simple_select commits whatever the user has drawn.
        // MapboxDraw needs ≥3 points to keep the polygon; if there are fewer
        // it discards. That's fine — same behavior as double-click finish.
        drawRef.current.changeMode('simple_select');
        setDrawMode('simple_select');
        emitChange();
    };
    const cancelDrawing = () => {
        if (!drawRef.current) return;
        // Cancel the *current* in-progress polygon only, not previously
        // committed ones. trash() with no selection deletes the in-progress
        // feature in draw_polygon mode.
        try { drawRef.current.trash(); } catch (_e) { /* noop */ }
        drawRef.current.changeMode('simple_select');
        setDrawMode('simple_select');
    };
    const undoLastPoint = () => {
        if (!drawRef.current) return;
        // MapboxDraw has no public undo API. Best UX is to simulate the
        // Backspace key, which the polygon mode listens for and pops the
        // last vertex.
        try {
            const evt = new KeyboardEvent('keydown', { key: 'Backspace', code: 'Backspace' });
            (containerRef.current?.querySelector('.mapboxgl-canvas') || document).dispatchEvent(evt);
        } catch (_e) { /* noop */ }
    };
    const clearAllPolygons = () => {
        if (!drawRef.current) return;
        if (!window.confirm('Delete all roof polygons you\u2019ve drawn? This can\u2019t be undone.')) return;
        drawRef.current.deleteAll();
        drawRef.current.changeMode('simple_select');
        setDrawMode('simple_select');
        emitChange();
    };

    const hasPolygons = (drawRef.current?.getAll()?.features?.length || 0) > 0;

    if (!MAPBOX_TOKEN) {
        return (
            <div className="border rounded-lg p-4 bg-amber-50 text-amber-900 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Mapbox token missing — set <code className="font-mono">REACT_APP_MAPBOX_TOKEN</code> in frontend/.env.
            </div>
        );
    }

    return (
        <div className="qf-roof-map">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span className="truncate max-w-[260px] sm:max-w-[420px]" title={address}>{address || 'No address'}</span>
                    {geocodeInfo && (() => {
                        const desc = describeGeocodeAccuracy(geocodeInfo);
                        const cls = desc.tone === 'high'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : desc.tone === 'medium'
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200';
                        return (
                            <Badge variant="outline" className={`text-[10px] font-normal ${cls}`} title={desc.detail} data-testid="roofmap-geocode-accuracy">
                                {desc.label}
                            </Badge>
                        );
                    })()}
                    {/* Pin-drift warning: appears when the contractor has dragged the
                        refine pin >75 m from the geocoded centroid. Most US
                        residential parcels are ≤60 m on the long side, so a drift
                        of 75+ m almost always means the pin is on a neighbouring
                        building. We escalate to "danger" at 150 m (definitely
                        wrong house). */}
                    {pinDriftM > 75 && (
                        <Badge
                            variant="outline"
                            className={`text-[10px] font-normal ${pinDriftM > 150 ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}
                            title={`Pin is ${Math.round(pinDriftM)} m from the geocoded address. Confirm you're on the right rooftop before running AI.`}
                            data-testid="roofmap-pin-drift-warning"
                        >
                            <AlertCircle className="h-3 w-3 mr-0.5" />
                            Pin {Math.round(pinDriftM)} m off parcel — verify rooftop
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                    <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5" data-testid="roofmap-style-toggle">
                        {[
                            { k: 'satellite-streets', label: 'Sat+Streets' },
                            { k: 'satellite', label: 'Satellite' },
                            { k: 'standard', label: 'Standard' },
                        ].map((s) => (
                            <button
                                key={s.k}
                                onClick={() => setStyle(s.k)}
                                className={`text-xs px-2 py-1 rounded ${style === s.k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid={`roofmap-style-${s.k}`}
                                type="button"
                            >
                                <Layers className="h-3 w-3 inline mr-0.5" />{s.label}
                            </button>
                        ))}
                    </div>
                    {!readOnly && (
                        <Button variant="outline" size="sm" onClick={resetView} data-testid="roofmap-reset-view" type="button">
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Re-center
                        </Button>
                    )}
                </div>
            </div>

            <div className="relative border border-slate-200 rounded-lg overflow-hidden bg-slate-100" style={{ height }} data-testid="roofmap-canvas-wrapper">
                <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-50/70">
                        <div className="flex items-center gap-2 text-slate-600 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading satellite imagery&hellip;
                        </div>
                    </div>
                )}
                {error && (
                    <div className="absolute inset-0 flex items-center justify-center bg-rose-50 text-rose-700 text-sm p-4">
                        <AlertCircle className="h-4 w-4 mr-2" /> {error}
                    </div>
                )}

                {/* While in draw mode, show a help banner overlaid on the map so
                    the user always knows what state they're in and how to finish.
                    This solves the previous "stuck after a few points, only way out
                    is the trash can which nukes everything" complaint. */}
                {!readOnly && drawMode === 'draw_polygon' && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-slate-900/95 text-white text-xs rounded-full px-3 py-1.5 shadow-lg flex items-center gap-2" data-testid="roofmap-draw-hint">
                        <Pencil className="h-3.5 w-3.5 text-amber-400" />
                        <span>Click around the roof. Hit <b>Finish</b> when done &mdash; or double-click the last point.</span>
                    </div>
                )}
            </div>

            {!readOnly && (
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap" data-testid="roofmap-toolbar">
                    {/* Left: status hint (changes based on whether user is drawing) */}
                    <div className="text-xs text-slate-600">
                        {drawMode === 'draw_polygon' ? (
                            <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                                <Pencil className="h-3 w-3" /> Drawing &mdash; click points around the roof
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-amber-200 align-middle" />
                                Drag the pin onto the rooftop, then click <b>Run AI Estimate</b> &mdash; or draw facets manually.
                            </span>
                        )}
                    </div>

                    {/* Right: contextual buttons (Draw / Finish / Cancel / Undo / Clear) */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {drawMode === 'draw_polygon' ? (
                            <>
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={finishDrawing}
                                    type="button"
                                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                                    data-testid="roofmap-finish-polygon"
                                >
                                    <Check className="h-3.5 w-3.5 mr-1" /> Finish
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={undoLastPoint}
                                    type="button"
                                    title="Undo last point"
                                    data-testid="roofmap-undo-point"
                                >
                                    <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo point
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={cancelDrawing}
                                    type="button"
                                    className="text-rose-600 hover:text-rose-700"
                                    data-testid="roofmap-cancel-polygon"
                                >
                                    <X className="h-3.5 w-3.5 mr-1" /> Cancel
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={startDrawing}
                                    type="button"
                                    data-testid="roofmap-start-drawing"
                                >
                                    <Pencil className="h-3.5 w-3.5 mr-1" /> Draw roof
                                </Button>
                                {hasPolygons && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearAllPolygons}
                                        type="button"
                                        className="text-rose-600 hover:text-rose-700"
                                        data-testid="roofmap-clear-all"
                                    >
                                        <X className="h-3.5 w-3.5 mr-1" /> Clear all
                                    </Button>
                                )}
                            </>
                        )}

                        <label className="text-xs text-slate-600 ml-2">Pitch:</label>
                        <select
                            value={defaultPitch}
                            onChange={(e) => setDefaultPitch(e.target.value)}
                            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                            data-testid="roofmap-pitch-select"
                        >
                            <option value="low">Low (~4/12)</option>
                            <option value="medium">Medium (~6/12)</option>
                            <option value="steep">Steep (~9/12)</option>
                        </select>
                    </div>
                </div>
            )}

            {measurements && (measurements.facets_count > 0 || readOnly) && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="roofmap-measurements">
                    <Stat label="Facets" value={measurements.facets_count} />
                    <Stat label="Footprint" value={`${measurements.footprint_sqft?.toLocaleString() || 0} sqft`} />
                    <Stat label="Roof area" value={`${measurements.roof_sqft?.toLocaleString() || 0} sqft`} highlight />
                    <Stat label="Pitch" value={measurements.pitch} className="capitalize" />
                </div>
            )}
        </div>
    );
});

function Stat({ label, value, className = '', highlight = false }) {
    return (
        <div className={`rounded-md border ${highlight ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'} px-3 py-2`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`text-sm font-semibold text-slate-900 ${className}`}>{value ?? '—'}</div>
        </div>
    );
}

// Draw styling — amber accent for visibility on satellite imagery
const drawStyles = [
    { id: 'gl-draw-polygon-fill-inactive', type: 'fill', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']], paint: { 'fill-color': '#F59E0B', 'fill-outline-color': '#B45309', 'fill-opacity': 0.25 } },
    { id: 'gl-draw-polygon-fill-active', type: 'fill', filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']], paint: { 'fill-color': '#F59E0B', 'fill-outline-color': '#B45309', 'fill-opacity': 0.35 } },
    { id: 'gl-draw-polygon-stroke-inactive', type: 'line', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#F59E0B', 'line-width': 2.5 } },
    { id: 'gl-draw-polygon-stroke-active', type: 'line', filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#FBBF24', 'line-width': 3, 'line-dasharray': [0.2, 2] } },
    { id: 'gl-draw-polygon-and-line-vertex-stroke-inactive', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 6, 'circle-color': '#fff' } },
    { id: 'gl-draw-polygon-and-line-vertex-inactive', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 4, 'circle-color': '#F59E0B' } },
    { id: 'gl-draw-line-inactive', type: 'line', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#F59E0B', 'line-width': 2 } },
];
